import type { Project } from '@/lib/mockData'
import PresaleProceedsSlider from '@/components/PresaleProceedsSlider'
import { BOND_THRESHOLD } from '@/lib/bondingProgress'
import { formatNumber } from '@/lib/format'
import {
  clampPresaleLpPct,
  creatorPctFromLp,
  formatBps,
  LAUNCHPAD_ALLOCATION,
  PRESALE_FEE_BPS,
  PRESALE_LAUNCH_FEE_ETH,
  PRESALE_LP_DEFAULT_PCT,
} from '@/lib/protocol'

interface Slice {
  label: string
  pct: number
  note: string
  color: string
}

function slicesFor(project: Project): Slice[] {
  if (project.type === 'launchpad') {
    return [
      { label: 'Bonding curve', pct: LAUNCHPAD_ALLOCATION.curvePct, note: 'Sold on-curve until graduation', color: 'bg-lime-400' },
      { label: 'Open-market LP', pct: LAUNCHPAD_ALLOCATION.lpPct, note: 'Seeded when the token bonds', color: 'bg-lime-300' },
      { label: 'Colony reserve', pct: LAUNCHPAD_ALLOCATION.reservePct, note: 'Held for ecosystem grants', color: 'bg-zinc-500' },
    ]
  }

  const lpPct = clampPresaleLpPct(project.lpPct ?? PRESALE_LP_DEFAULT_PCT)
  const creatorPct = creatorPctFromLp(lpPct)

  return [
    { label: 'Buyer tokens', pct: 100, note: project.tokensPerEth ? `${formatNumber(project.tokensPerEth)} tokens per ETH contributed` : 'Minted to contributors at the sale rate after finalize', color: 'bg-orange-400' },
    { label: 'Listing rate', pct: 0, note: project.listingRate ? `${formatNumber(project.listingRate)} tokens per ETH for LP seeding` : 'Used when remaining ETH seeds liquidity', color: 'bg-orange-300' },
    { label: 'Creator ETH', pct: creatorPct, note: `${creatorPct}% of remaining ETH after the 3% platform fee — no claimTeam()`, color: 'bg-zinc-500' },
  ]
}

function TokenomicsPanel({ project }: { project: Project }) {
  const slices = slicesFor(project)
  const isLaunchpad = project.type === 'launchpad'

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md">
      <h2 className="text-sm font-semibold text-white">Tokenomics</h2>
      <p className="mt-1 text-xs text-zinc-500">
        {isLaunchpad
          ? `Linear curve until ${Math.round(BOND_THRESHOLD * 100)}% of max supply is sold.`
          : `Opening a presale costs ${PRESALE_LAUNCH_FEE_ETH} ETH. Penguin Pad takes ${formatBps(PRESALE_FEE_BPS)} of ETH raised; remaining ETH splits to LP and creator.`}
      </p>

      <div className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-zinc-900" aria-hidden="true">
        {slices.map(slice => (
          <div key={slice.label} className={slice.color} style={{ width: `${slice.pct}%` }} />
        ))}
      </div>

      <ul className="mt-4 space-y-3">
        {slices.map(slice => (
          <li key={slice.label} className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${slice.color}`} aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-white">{slice.label}</p>
                <p className="text-xs text-zinc-500">{slice.note}</p>
              </div>
            </div>
            <span className="text-sm font-semibold text-zinc-300">
              {slice.pct}
              %
            </span>
          </li>
        ))}
      </ul>

      {!isLaunchpad
        ? (
            <div className="mt-5">
              <PresaleProceedsSlider
                lpPct={clampPresaleLpPct(project.lpPct ?? PRESALE_LP_DEFAULT_PCT)}
                readOnly
              />
            </div>
          )
        : null}

      {project.maxSupply
        ? (
            <p className="mt-4 text-[11px] text-zinc-600">
              {isLaunchpad ? 'Max supply' : 'Estimated supply'}
              {' '}
              {formatNumber(project.maxSupply)}
              {' '}
              {project.ticker}
              {isLaunchpad
                ? '. Curve fills first; leftover supply seeds LP and the colony reserve.'
                : '. Creator-entered total supply; tokens per ETH and listing rate are derived from supply ÷ hard cap.'}
            </p>
          )
        : null}
    </div>
  )
}

export default TokenomicsPanel
